import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { isNativeApp } from './platform';

/** Deckungsgleich mit `UPLOAD_MAX_FILES` in server.js und Multer. */
export const PICK_IMAGES_MAX_FILES = 100;

/** Web-`<input>` und FilePicker: nur Bilder. */
export const PICK_IMAGES_ACCEPT = 'image/*';

const ALLOWED_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

const MIME_TO_EXT = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
};

/**
 * Liest die Auswahl aus einem `<input type="file">` (Web-Zweig).
 * @param {HTMLInputElement|null|undefined} input
 * @returns {File[]}
 */
export function filesFromFileInput(input) {
    if (!input || !input.files) {
        return [];
    }
    return Array.from(input.files);
}

export function clearFileInput(input) {
    if (input) {
        input.value = '';
    }
}

/** Nutzer hat Galerie oder Kamera abgebrochen — kein Fehler. */
export function isPickerCanceled(err) {
    const msg = String(err?.message || err || '').toLowerCase();
    const code = String(err?.code || '').toLowerCase();
    return /cancel/.test(msg) || /cancel/.test(code);
}

/**
 * Web: klickt das vorhandene `<input type="file" multiple accept="image/*">`.
 * Native: FilePicker mit Tap-Reihenfolge (iOS `ordered`) und HEIC→JPEG.
 *
 * @param {{ input?: HTMLInputElement }} [options]
 * @returns {Promise<File[]>}
 */
export async function pickImages({ input } = {}) {
    if (isNativeApp()) {
        return pickImagesNative();
    }
    return pickImagesWeb(input);
}

/** Eine Aufnahme mit der Gerätekamera. Nur nativ sinnvoll. */
export async function takeCameraPhoto() {
    if (!isNativeApp()) {
        return [];
    }
    try {
        await Camera.requestPermissions({ permissions: ['camera'] });
        const photo = await Camera.getPhoto({
            quality: 90,
            resultType: CameraResultType.Uri,
            source: CameraSource.Camera,
            correctOrientation: true,
            saveToGallery: false,
        });
        const file = await cameraPhotoToFile(photo, 0);
        return file ? [file] : [];
    } catch (err) {
        if (isPickerCanceled(err)) {
            return [];
        }
        throw err;
    }
}

function pickImagesWeb(input) {
    if (!input) {
        return Promise.resolve([]);
    }
    return new Promise((resolve) => {
        input.addEventListener(
            'change',
            () => {
                resolve(filesFromFileInput(input));
            },
            { once: true },
        );
        input.click();
    });
}

async function pickImagesNative() {
    try {
        // Android unterstützt bei `limit` nur 0 oder 1; Cap liegt bei 0 (unbegrenzt),
        // die JS-Seite begrenzt auf PICK_IMAGES_MAX_FILES.
        const limit =
            Capacitor.getPlatform() === 'ios' ? PICK_IMAGES_MAX_FILES : 0;
        const { files } = await FilePicker.pickImages({
            ordered: true,
            skipTranscoding: false,
            readData: false,
            limit,
        });
        const picked = Array.isArray(files) ? files : [];
        // Indexbasiert — nie `push` in Completion-Handlern (iOS-Race im Plugin).
        return Promise.all(
            picked.map((entry, index) => pickedEntryToFile(entry, index)),
        );
    } catch (err) {
        if (isPickerCanceled(err)) {
            return [];
        }
        throw err;
    }
}

async function pickedEntryToFile(entry, index) {
    if (entry?.blob instanceof Blob) {
        return blobToImageFile(entry.blob, entry.name, entry.mimeType, index);
    }
    if (!entry?.path) {
        throw new Error('Kein Dateipfad vom Foto-Picker');
    }
    const blob = await blobFromNativePath(entry.path);
    return blobToImageFile(
        blob,
        entry.name,
        entry.mimeType || blob.type,
        index,
    );
}

async function cameraPhotoToFile(photo, index) {
    const src =
        photo?.webPath ||
        (photo?.path ? Capacitor.convertFileSrc(photo.path) : '');
    if (!src) {
        throw new Error('Kein Kamerabild');
    }
    const response = await fetch(src);
    if (!response.ok) {
        throw new Error('Kamerabild konnte nicht gelesen werden');
    }
    const blob = await response.blob();
    const mime = photo?.format
        ? `image/${String(photo.format).toLowerCase()}`
        : blob.type;
    return blobToImageFile(blob, `camera-${Date.now()}.jpg`, mime, index);
}

async function blobFromNativePath(path) {
    const url = Capacitor.convertFileSrc(path);
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Bild konnte nicht gelesen werden');
    }
    return response.blob();
}

function blobToImageFile(blob, name, mimeType, index) {
    const type = normalizeImageMime(mimeType, blob.type, name);
    const fileName = imageFileName(name, type, index);
    return new File([blob], fileName, {
        type,
        lastModified: Date.now(),
    });
}

function normalizeImageMime(mime, blobType, name) {
    const raw = `${mime || ''} ${blobType || ''} ${name || ''}`.toLowerCase();
    if (raw.includes('heic') || raw.includes('heif')) {
        return 'image/jpeg';
    }
    if (raw.includes('image/png') || raw.endsWith('.png')) {
        return 'image/png';
    }
    if (raw.includes('image/webp') || raw.endsWith('.webp')) {
        return 'image/webp';
    }
    if (raw.includes('image/gif') || raw.endsWith('.gif')) {
        return 'image/gif';
    }
    return 'image/jpeg';
}

function imageFileName(name, mime, index) {
    const ext = MIME_TO_EXT[(mime || '').toLowerCase()] || 'jpg';
    const raw = String(name || '').trim();
    const currentExt = raw.includes('.')
        ? raw.split('.').pop().toLowerCase()
        : '';
    if (raw && ALLOWED_IMAGE_EXTS.includes(currentExt)) {
        return raw;
    }
    const base = raw.replace(/\.[^.]+$/, '') || `image${index + 1}`;
    return `${base}.${ext}`;
}
